import { SaveQuantifyOrCompareForm } from '@modules/analysis/components/SaveQuantifyOrCompareForm';
import { isSavedAnalysis } from '@modules/analysis/helpers';
import { useDashboardToAddToFromParam } from '@modules/analysis/hooks';
import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { CanAccess } from '@modules/common/components/CanAccess';
import { CopyArtefactLinkButton } from '@modules/common/components/CopyArtefactLinkButton';
import { ArtefactType } from '@modules/common/types';
import { AddAnalysisToDashboardButton } from '@modules/dashboard/components';
import { useGetQuantifyCompareQuery } from '@modules/quantify/hooks';
import { useGetQuantifyTableEQLProps } from '@modules/quantify/hooks/useFetchQuantifyTableData/useGetQuantifyTableEQLProps';
import { useGetQuantifyQueryMetadata } from '@modules/quantify/hooks/useGetQuantifyQueryMetadata';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { isValidQuery } from '@modules/quantify/utils';
import { Box } from '@mui/material';
import {
  AnalysisUseCase,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useCallback, useMemo } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { shallow } from 'zustand/shallow';
import { QuantifySaveActions, ResetQuantifyAction } from '../actions';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');

export const useGetQuantifyRightActions = () => {
  const { id, title, href } = useAnalysisStore(
    (analysis) => ({
      id: analysis.id,
      title: analysis.title,
      scope: analysis.scope,
      href: analysis.href,
    }),
    shallow,
  );

  const metadata = useGetQuantifyQueryMetadata();
  const eqlProps = useGetQuantifyTableEQLProps();

  const query = useQuantifyStore(({ query }) => query);

  const groupByLabel = useQuantifyStore(
    ({ query }) => query.groupByField?.displayName,
  );
  const shouldShowAnomalies = useQuantifyStore(
    ({ query }) => query.shouldShowAnomalies,
  );
  const compareQuery = useGetQuantifyCompareQuery();
  const useCase = compareQuery
    ? AnalysisUseCase.Compare
    : AnalysisUseCase.Default;

  const { onSaveAnalysis } = useSaveAnalysis({
    eqlProps,
    metadata,
    eventProps: {
      quantifyQueryOptions: {
        showMeLabel: groupByLabel,
        shouldShowAnomalies,
      },
    },
    useCase,
  });

  const onCopyClick = useCallback(
    (link: string) => {
      trackQuantifyReportEvents({
        eventName: 'Quantify Report - Share Link Copied',
        name: title,
        link,
      });
    },
    [title],
  );

  const isAllowedToSave = isValidQuery(query);

  const { dashboardToAddToFromParam } = useDashboardToAddToFromParam();

  const shouldShowAddToDashboardButton = !Boolean(dashboardToAddToFromParam);

  const rightActions = useMemo(
    () => [
      {
        component: <ResetQuantifyAction />,
        key: 'Reset Quantify',
      },
      ...(shouldShowAddToDashboardButton
        ? [
            {
              component: (
                <CanAccess
                  resource={PermissionResource.Artefact}
                  operation={PermissionOperation.Write}
                >
                  <Box>
                    <AddAnalysisToDashboardButton
                      isDisabled={!isAllowedToSave}
                      renderAnalysisFormDialog={(params) => {
                        return <SaveQuantifyOrCompareForm {...params} />;
                      }}
                    />
                  </Box>
                </CanAccess>
              ),
              key: 'Add Quantify to Dashboard',
            },
          ]
        : []),
      {
        component: (
          <CopyArtefactLinkButton
            artefactType={ArtefactType.Quantify}
            artefactLink={href}
            onClick={onCopyClick}
            onSave={onSaveAnalysis}
            isSavedArtefact={isSavedAnalysis(id)}
            isDisabled={!isAllowedToSave}
            className="quantify-copy-link-button"
          />
        ),
        key: 'Copy Analysis Link',
      },
      {
        component: <QuantifySaveActions />,
        key: 'Save Quantify',
      },
    ],
    [
      shouldShowAddToDashboardButton,
      href,
      id,
      isAllowedToSave,
      onCopyClick,
      onSaveAnalysis,
    ],
  );

  return rightActions;
};
